var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { useEffect, useState } from 'react';
import useAppComponents from '@/hooks/useAppComponents';
import { delay } from '@/utils/time';
export function useUpdateUser({ initialValues, accessToken, enforceBirthYearRange = false, }) {
    const { api } = useAppComponents();
    const initialFormValues = initialValues !== null && initialValues !== void 0 ? initialValues : {
        nickname: '',
        email: '',
        birthYear: '',
        phoneNumber: '',
        isPhoneVerified: false,
        mobilityTools: [],
        isNewsLetterSubscriptionAgreed: false,
    };
    const [formValue, setFormValue] = useState(initialFormValues);
    const [formState, setFormState] = useState({});
    const validateNickname = (nickname) => __awaiter(this, void 0, void 0, function* () {
        if (nickname.length < 2) {
            return '닉네임은 최소 2글자 이상이어야 합니다.';
        }
        if (nickname.length > 15) {
            return '닉네임은 최대 15글자 이하여야 합니다.';
        }
        if (!/^[a-zA-Z가-힣0-9]+$/.test(nickname)) {
            return '닉네임은 영어, 한글, 숫자만 사용 가능해요.';
        }
        try {
            yield delay(200);
            const response = yield api.validateUserProfilePost({
                nickname,
            }, {
                headers: Object.assign({}, (accessToken && {
                    Authorization: `Bearer ${accessToken}`,
                })),
            });
            if (response.data.nicknameErrorMessage) {
                return response.data.nicknameErrorMessage;
            }
        }
        catch (error) {
            console.error(error);
            return '중복 확인에 실패했어요. 잠시 후 다시 시도해주세요.';
        }
        return undefined;
    });
    const validateEmail = (email) => __awaiter(this, void 0, void 0, function* () {
        if (email.length < 6) {
            return '이메일은 최소 6글자 이상이어야 합니다.';
        }
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
            return '올바른 이메일 형식을 입력해주세요.';
        }
        try {
            yield delay(200);
            const response = yield api.validateUserProfilePost({
                email,
            }, {
                headers: Object.assign({}, (accessToken && {
                    Authorization: `Bearer ${accessToken}`,
                })),
            });
            if (response.data.emailErrorMessage) {
                return response.data.emailErrorMessage;
            }
        }
        catch (error) {
            console.error(error);
            return '중복 확인에 실패했어요. 잠시 후 다시 시도해주세요.';
        }
        return undefined;
    });
    const validateBirthYear = (birthYear) => {
        if (!birthYear) {
            return '출생년도를 입력해주세요.';
        }
        if (!/^\d{4}$/.test(birthYear)) {
            return '숫자로만 4자리 입력해주세요.';
        }
        if (enforceBirthYearRange) {
            const year = parseInt(birthYear, 10);
            if (year < 1900 || year > 2026) {
                return '출생년도는 1900년부터 2026년 사이여야 합니다.';
            }
        }
        return undefined;
    };
    const validateMobilityTools = (mobilityTools) => {
        if (mobilityTools.length === 0) {
            return '이동유형을 선택해주세요.';
        }
        return undefined;
    };
    // Validate nickname whenever it changes
    useEffect(() => {
        if (formValue.nickname === initialFormValues.nickname) {
            setFormState(prev => (Object.assign(Object.assign({}, prev), { nickname: undefined })));
            return;
        }
        let isSubscribed = true;
        const validate = () => __awaiter(this, void 0, void 0, function* () {
            setFormState(prev => (Object.assign(Object.assign({}, prev), { nickname: 'PROGRESS' })));
            const error = yield validateNickname(formValue.nickname);
            if (isSubscribed) {
                setFormState(prev => (Object.assign(Object.assign({}, prev), { nickname: error ? { errorMessage: error } : 'VALID' })));
            }
        });
        validate();
        return () => {
            isSubscribed = false;
        };
    }, [formValue.nickname]);
    // Validate email whenever it changes
    useEffect(() => {
        if (formValue.email === initialFormValues.email) {
            setFormState(prev => (Object.assign(Object.assign({}, prev), { email: undefined })));
            return;
        }
        let isSubscribed = true;
        const validate = () => __awaiter(this, void 0, void 0, function* () {
            setFormState(prev => (Object.assign(Object.assign({}, prev), { email: 'PROGRESS' })));
            const error = yield validateEmail(formValue.email);
            if (isSubscribed) {
                setFormState(prev => (Object.assign(Object.assign({}, prev), { email: error ? { errorMessage: error } : 'VALID' })));
            }
        });
        validate();
        return () => {
            isSubscribed = false;
        };
    }, [formValue.email]);
    // Validate birth year whenever it changes
    useEffect(() => {
        if (formValue.birthYear === initialFormValues.birthYear) {
            setFormState(prev => (Object.assign(Object.assign({}, prev), { birthYear: undefined })));
            return;
        }
        const error = validateBirthYear(formValue.birthYear);
        setFormState(prev => (Object.assign(Object.assign({}, prev), { birthYear: error ? { errorMessage: error } : 'VALID' })));
    }, [formValue.birthYear]);
    useEffect(() => {
        if (formValue.mobilityTools === initialFormValues.mobilityTools) {
            setFormState(prev => (Object.assign(Object.assign({}, prev), { mobilityTools: undefined })));
            return;
        }
        const error = validateMobilityTools(formValue.mobilityTools);
        setFormState(prev => (Object.assign(Object.assign({}, prev), { mobilityTools: error ? { errorMessage: error } : 'VALID' })));
    }, [formValue.mobilityTools]);
    useEffect(() => {
        if (formValue.isNewsLetterSubscriptionAgreed ===
            initialFormValues.isNewsLetterSubscriptionAgreed) {
            setFormState(prev => (Object.assign(Object.assign({}, prev), { isNewsLetterSubscriptionAgreed: undefined })));
            return;
        }
        setFormState(prev => (Object.assign(Object.assign({}, prev), { isNewsLetterSubscriptionAgreed: 'VALID' })));
    }, [formValue.isNewsLetterSubscriptionAgreed]);
    const updateField = (field, value) => __awaiter(this, void 0, void 0, function* () {
        setFormValue(prev => (Object.assign(Object.assign({}, prev), { [field]: value })));
    });
    const submit = () => __awaiter(this, void 0, void 0, function* () {
        const isAllFieldsValid = Object.values(formState).every(field => field === 'VALID' || field === undefined);
        if (!isAllFieldsValid) {
            throw new Error('모든 필드가 유효하지 않습니다.');
        }
        const parsedBirthYear = formValue.birthYear
            ? parseInt(formValue.birthYear, 10)
            : undefined;
        if (parsedBirthYear && isNaN(parsedBirthYear)) {
            throw new Error('출생연도가 유효하지 않습니다.');
        }
        const user = (yield api.updateUserInfoPost({
            email: formValue.email,
            nickname: formValue.nickname,
            mobilityTools: formValue.mobilityTools,
            birthYear: parsedBirthYear,
            isNewsLetterSubscriptionAgreed: formValue.isNewsLetterSubscriptionAgreed,
        }, {
            headers: accessToken
                ? { Authorization: `Bearer ${accessToken}` }
                : undefined,
        })).data.user;
        return user;
    });
    return {
        formValue,
        formState,
        updateField,
        submit,
    };
}
